/**
 * WebAuthn Helper Functions for Frontend Integration
 * 
 * This file provides utility functions to integrate WebAuthn authentication
 * into your React frontend.
 */

// Helper function to convert base64 string to Uint8Array
export function base64ToUint8Array(base64) {
  const binary = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// Helper function to convert Uint8Array to base64 string
export function uint8ArrayToBase64(uint8Array) {
  let binary = '';
  const len = uint8Array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Register a new WebAuthn credential (passkey or FIDO token)
 * @param {string} username - The username to register for
 * @returns {Promise<object>} Registration result
 */
export async function registerWebAuthn(username) {
  try {
    // Start registration
    const startResponse = await fetch('/api/webauthn/register/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username })
    });
    
    if (!startResponse.ok) {
      const error = await startResponse.json();
      throw new Error(error.error || 'Registration start failed');
    }
    
    const options = await startResponse.json();
    
    // Convert base64 strings to ArrayBuffers for WebAuthn API
    options.challenge = base64ToUint8Array(options.challenge);
    options.user.id = base64ToUint8Array(options.user.id);
    
    if (options.excludeCredentials) {
      options.excludeCredentials = options.excludeCredentials.map(cred => ({
        ...cred,
        id: base64ToUint8Array(cred.id)
      }));
    }
    
    // Create credential using WebAuthn API
    const credential = await navigator.credentials.create({ publicKey: options });
    
    if (!credential) {
      throw new Error('Credential creation was cancelled');
    }
    
    // Convert credential to format expected by server
    const credentialForServer = {
      id: credential.id,
      rawId: uint8ArrayToBase64(new Uint8Array(credential.rawId)),
      response: {
        clientDataJSON: uint8ArrayToBase64(new Uint8Array(credential.response.clientDataJSON)),
        attestationObject: uint8ArrayToBase64(new Uint8Array(credential.response.attestationObject)),
        transports: credential.response.getTransports ? credential.response.getTransports() : []
      },
      type: credential.type
    };
    
    // Complete registration
    const finishResponse = await fetch('/api/webauthn/register/finish', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(credentialForServer)
    });
    
    if (!finishResponse.ok) {
      const error = await finishResponse.json();
      throw new Error(error.error || 'Registration verification failed');
    }
    
    return await finishResponse.json();
  } catch (error) {
    console.error('WebAuthn registration error:', error);
    throw error;
  }
}

/**
 * Authenticate using WebAuthn
 * @param {string} username - The username to authenticate
 * @returns {Promise<object>} Authentication result
 */
export async function loginWebAuthn(username) {
  try {
    // Start authentication
    const startResponse = await fetch('/api/webauthn/login/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username })
    });
    
    if (!startResponse.ok) {
      const error = await startResponse.json();
      throw new Error(error.error || 'Authentication start failed');
    }
    
    const options = await startResponse.json();
    
    // Convert base64 strings to ArrayBuffers
    options.challenge = base64ToUint8Array(options.challenge);
    
    if (options.allowCredentials) {
      options.allowCredentials = options.allowCredentials.map(cred => ({
        ...cred,
        id: base64ToUint8Array(cred.id)
      }));
    }
    
    // Get credential using WebAuthn API
    const credential = await navigator.credentials.get({ publicKey: options });
    
    if (!credential) {
      throw new Error('Authentication was cancelled');
    }
    
    // Convert credential to format expected by server
    const credentialForServer = {
      id: credential.id,
      rawId: uint8ArrayToBase64(new Uint8Array(credential.rawId)),
      response: {
        clientDataJSON: uint8ArrayToBase64(new Uint8Array(credential.response.clientDataJSON)),
        authenticatorData: uint8ArrayToBase64(new Uint8Array(credential.response.authenticatorData)),
        signature: uint8ArrayToBase64(new Uint8Array(credential.response.signature)),
        userHandle: credential.response.userHandle 
          ? uint8ArrayToBase64(new Uint8Array(credential.response.userHandle)) 
          : null
      },
      type: credential.type
    };
    
    // Complete authentication
    const finishResponse = await fetch('/api/webauthn/login/finish', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(credentialForServer)
    });
    
    if (!finishResponse.ok) {
      const error = await finishResponse.json();
      throw new Error(error.error || 'Authentication verification failed');
    }
    
    return await finishResponse.json();
  } catch (error) {
    console.error('WebAuthn authentication error:', error);
    throw error;
  }
}

/**
 * Check what authentication methods are available for a user
 * @param {string} username - The username to check
 * @returns {Promise<object>} Available authentication methods
 */
export async function getAuthMethods(username) {
  const response = await fetch(`/api/auth/methods?username=${encodeURIComponent(username)}`, {
    credentials: 'include'
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get auth methods');
  }
  
  return await response.json();
}

/**
 * Setup TOTP for a user
 * @param {string} username - The username to setup TOTP for
 * @returns {Promise<object>} TOTP setup data including QR code
 */
export async function setupTOTP(username) {
  const response = await fetch('/api/totp/setup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'TOTP setup failed');
  }
  
  return await response.json();
}

/**
 * Verify TOTP setup by providing a code
 * @param {string} code - The 6-digit TOTP code
 * @returns {Promise<object>} Verification result
 */
export async function verifyTOTPSetup(code) {
  const response = await fetch('/api/totp/verify-setup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'TOTP verification failed');
  }
  
  return await response.json();
}

/**
 * Login using TOTP
 * @param {string} username - The username
 * @param {string} code - The 6-digit TOTP code
 * @returns {Promise<object>} Login result
 */
export async function loginTOTP(username, code) {
  const response = await fetch('/api/totp/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, code })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'TOTP login failed');
  }
  
  return await response.json();
}

/**
 * Check if WebAuthn is supported in the current browser
 * @returns {boolean} True if WebAuthn is supported
 */
export function isWebAuthnSupported() {
  return (
    window.PublicKeyCredential !== undefined &&
    navigator.credentials !== undefined &&
    typeof navigator.credentials.create === 'function' &&
    typeof navigator.credentials.get === 'function'
  );
}
